import { getServiceLanding } from '@/lib/serviceLandingPages';
import { ServiceMobileBar } from '@/components/trinity/ServiceConversion';
import React, { useEffect, useRef, useState, lazy, Suspense } from 'react';
import { captureLeadAttribution } from '@/lib/leadTracking';
import ImageProtection from '@/components/ImageProtection';
import { AIPersonalizationProvider } from '@/components/personalization/AIPersonalizationEngine';
import { useMeasurement } from '@/components/hooks/useMeasurement';
import useAfterFirstPaint from '@/components/hooks/useAfterFirstPaint';
import GlobalPageSchema from '@/components/seo/GlobalPageSchema';
import { getPageSocialImage } from '@/lib/seo/socialImages';
import NoIndexGuard from '@/components/seo/NoIndexGuard';
import BreadcrumbSchema from '@/components/seo/BreadcrumbSchema';
import WidgetErrorBoundary from '@/components/engagement/WidgetErrorBoundary';
import CookieConsent from '@/components/CookieConsent';
import SignupConversion from '@/components/analytics/SignupConversion';
import { appParams } from '@/lib/app-params';

// Personalized blog recommendations only ever render for a signed-in visitor
// (the component bails on its own auth check), so anonymous traffic — which is
// almost all of it on a public practice site — must not download the chunk.
const HAS_SESSION = Boolean(appParams?.token);

// Lazy load non-critical layout components. FloatingCTA and OnboardingTour
// are the only framer-motion users left on the eager path; loading them
// after first render keeps that library (~100 KB compressed) out of the
// entry bundle for every page.
const FloatingCTA = lazy(() => import('@/components/engagement/FloatingCTA'));
const OnboardingTour = lazy(() => import('@/components/onboarding/OnboardingTour'));
const SmartCTAEngine = lazy(() => import('@/components/personalization/SmartCTAEngine'));
const PersonalizedBlogRecommendations = lazy(() => import('@/components/personalization/PersonalizedBlogRecommendations'));
// Invisible support widgets: their code is only fetched once the page is
// interactive, so a phone never parses them to show the hero.
const SMSWidget = lazy(() => import('@/components/engagement/SMSWidget'));

const SERVICE_LANDING_PAGES = new Set([
  'DentalImplants', 'CosmeticDentistry', 'Invisalign', 'FullMouthRestoration',
  'EmergencyDentistry', 'GeneralDentistry', 'PreventativeDentistry', 'SedationDentistry',
  'LaserDentistry', 'RegenerativeDentistry', 'OralSurgery', 'PeriodontalDiseaseTreatment',
]);

export default function Layout({ children, currentPageName }) {
  const isAdmin = window.location.pathname.startsWith('/admin/');
  const serviceLanding = getServiceLanding(window.location.pathname);
  const isServiceLanding = !isAdmin && (Boolean(serviceLanding) || SERVICE_LANDING_PAGES.has(currentPageName));
  const [hasConsultationQuiz, setHasConsultationQuiz] = useState(false);
  const contentRef = useRef(null);
  const suppressEngagement = Boolean(serviceLanding) || hasConsultationQuiz || ['Consult', 'ThankYou', 'BookAppointment'].includes(currentPageName);
  // One source owns sanitized page views and generic interaction counts.
  useMeasurement();
  // Floating widgets mount one frame after the page has painted so their
  // code-split chunks never race the first paint (see useAfterFirstPaint).
  const afterPaint = useAfterFirstPaint();

  // Bypass block landmark (WCAG 2.4.1): reuse a page's own <main> if it has
  // one, otherwise promote the content wrapper so the skip link lands on the
  // page content instead of the navigation. Avoids nesting two main landmarks.
  useEffect(() => {
    const root = contentRef.current;
    if (!root) return;
    const target = root.querySelector('main') || root;
    if (!target.id) target.id = 'main-content';
    if (target !== root && root.id === 'main-content') root.removeAttribute('id');
    if (target.tagName !== 'MAIN') target.setAttribute('role', 'main');
    target.setAttribute('tabindex', '-1');
  }, [currentPageName]);

  // Legacy guide pages do not all render PageMeta. Apply the same image
  // registry here so every listed landing page receives its social card.
  const socialImagePath = window.location.pathname;
  useEffect(() => {
    const image = getPageSocialImage(socialImagePath);
    if (!image) return;
    const tags = [
      ['property', 'og:image', image.url],
      ['property', 'og:image:secure_url', image.url],
      ['property', 'og:image:width', String(image.width)],
      ['property', 'og:image:height', String(image.height)],
      ['property', 'og:image:alt', image.alt],
      ['name', 'twitter:card', 'summary_large_image'],
      ['name', 'twitter:image', image.url],
      ['name', 'twitter:image:alt', image.alt],
    ];
    for (const [attribute, key, value] of tags) {
      const matches = [...document.head.querySelectorAll(`meta[${attribute}="${key}"]`)];
      const element = matches.shift() || document.createElement('meta');
      element.setAttribute(attribute, key);
      element.setAttribute('content', value);
      if (!element.parentNode) document.head.appendChild(element);
      matches.forEach(duplicate => duplicate.remove());
    }
  }, [socialImagePath]);

  // Keep the consultation area clear even when a quiz loads asynchronously.
  useEffect(() => {
    for (const key of ['trinity-form-submissions','trinity-demographics','treatment_finder_history','location_captured']) {
      try { localStorage.removeItem(key); } catch {}
    }
    captureLeadAttribution();
    let observer = null;
    let queued = false;
    const updateQuizPresence = () => {
      queued = false;
      const present = Boolean(document.querySelector('[data-consultation-quiz]'));
      setHasConsultationQuiz(present);
      // Once the quiz has mounted the document-wide observer has done its job;
      // leaving it running re-queries the whole page on every DOM mutation.
      if (present && observer) observer.disconnect();
    };
    // A first page render produces hundreds of mutation batches; running the
    // document-wide query once per frame instead of once per batch keeps that
    // work from becoming a long task during the mount.
    const schedulePresenceCheck = () => {
      if (queued) return;
      queued = true;
      if (typeof window.requestAnimationFrame === 'function') window.requestAnimationFrame(updateQuizPresence);
      else setTimeout(updateQuizPresence, 16);
    };
    updateQuizPresence();
    observer = new MutationObserver(schedulePresenceCheck);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer?.disconnect();
  }, [currentPageName]);

  // Consent-gated SDK loading is owned by useMeasurement/marketingConsent.
  // Page metadata is owned locally; the retired GHL SEO injector stays removed.

  // Defer third-party scripts until after critical rendering
  useEffect(() => {
      // Trusted Types 'default' policy — DOM-XSS backstop. The previous version
      // was an identity passthrough, which neutralized Trusted Types entirely.
      // CSP does not yet set `require-trusted-types-for 'script'`, so this only
      // takes effect if/when that directive is enabled — but it must not be a
      // no-op when it does.
      if (window.trustedTypes && window.trustedTypes.createPolicy) {
        // Mirrors the CSP script-src allow-list in index.html, plus origins
        // injected by inline tracker bootstraps.
        const SCRIPT_ORIGIN_ALLOWLIST = [
          window.location.origin,
          'https://app.hipaatizer.com',
          'https://www.googletagmanager.com',
          'https://www.google-analytics.com',
          'https://www.googleadservices.com',
          'https://googleads.g.doubleclick.net',
          'https://reputationhub.site',
          'https://link.growdental.ai',
          'https://www.localmarketingmanager.com',
          'https://files.withcherry.com',
          'https://api.opticl.ai',
          'https://seo-tools.leadconnectorhq.com',
          'https://s.vibe.co',
          'https://www.google.com',
          'https://www.gstatic.com',
          'https://app.retargetiq.com',
          'https://d-code.liadm.com',
        ];
        const sanitizeHTML = (input) => String(input)
          .replace(/<script\b[^>]*>[\s\S]*?<\/script\s*>/gi, '')
          .replace(/<script\b[^>]*\/?>/gi, '')
          .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '')
          .replace(/(href|src)\s*=\s*(["']?)\s*javascript:[^"'>\s]*/gi, '$1=$2#');
        try {
          window.trustedTypes.createPolicy('default', {
            createHTML: (input) => sanitizeHTML(input),
            createScript: (input) => {
              // CSP script-src still governs execution. Logged so stray
              // eval()-style sinks are visible before enforcement is enabled.
              console.warn('[TT] default createScript used');
              return String(input);
            },
            createScriptURL: (input) => {
              try {
                const url = new URL(String(input), window.location.origin);
                if (SCRIPT_ORIGIN_ALLOWLIST.includes(url.origin)) return url.href;
              } catch { /* invalid URL — fall through to reject */ }
              throw new TypeError('[TT] Blocked untrusted script URL: ' + input);
            },
          });
        } catch {
          // Policy already exists
        }
      }

      // Defer non-critical scripts until after page load
      const loadNonCriticalScripts = () => {
        // Skip all tracking scripts on admin pages
        if (window.location.pathname.startsWith('/admin/')) return;
        // NOTE: These tracking IDs are PUBLIC and meant to be visible client-side for analytics
        // They do not grant any privileged access - they only enable tracking on this domain



      };

      // Defer script loading until after LCP
      if (document.readyState === 'complete') {
        setTimeout(loadNonCriticalScripts, 0);
      } else {
        window.addEventListener('load', () => setTimeout(loadNonCriticalScripts, 0));
      }
    }, []);

  // Inject critical CSS and optimization headers
  useEffect(() => {
    if (!document.querySelector('style[data-critical-css]')) {
      const criticalCSS = document.createElement('style');
      criticalCSS.setAttribute('data-critical-css', 'true');
      criticalCSS.textContent = `
        /* Critical above-the-fold styles */
        body{margin:0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;text-rendering:optimizeLegibility}
        *{box-sizing:border-box}
        .container{width:100%;max-width:1280px;margin:0 auto;padding:0 1.5rem}
        @media(min-width:1024px){.container{padding:0 3rem}}
        img{max-width:100%;height:auto}
        a{color:inherit;text-decoration:none}
        h1,h2,h3{font-weight:300;letter-spacing:-0.02em}
        p{max-width:65ch}
      `;
      document.head.insertBefore(criticalCSS, document.head.firstChild);
    }

    // Server-side optimization and compression hints
    const scheduleTask = window.requestIdleCallback || ((cb) => setTimeout(cb, 1));
    if (!isAdmin) scheduleTask(() => {
      const metaTags = [
        { httpEquiv: 'Cache-Control', content: 'public, max-age=31536000, immutable' },
        { httpEquiv: 'X-Content-Type-Options', content: 'nosniff' },
        { httpEquiv: 'Content-Encoding', content: 'gzip, deflate, br' },
        { name: 'referrer', content: 'strict-origin-when-cross-origin' },
        { name: 'resource-type', content: 'document' },
        { name: 'compression', content: 'br' }
      ];

      metaTags.forEach(({ httpEquiv, name, content }) => {
        const selector = httpEquiv ? `meta[http-equiv="${httpEquiv}"]` : `meta[name="${name}"]`;
        if (!document.querySelector(selector)) {
          const meta = document.createElement('meta');
          if (httpEquiv) meta.httpEquiv = httpEquiv;
          if (name) meta.name = name;
          meta.content = content;
          document.head.appendChild(meta);
        }
      });
    });
  }, []);

  useEffect(() => {
    if (isAdmin) return;
    // Helper for scheduling non-critical tasks
    const scheduleTask = window.requestIdleCallback || ((cb) => setTimeout(cb, 1));

    // Essential meta tags for AI crawlers and SEO
      const metaTags = [
        { name: 'viewport', content: 'width=device-width, initial-scale=1.0, maximum-scale=5.0' },
        { name: 'charset', content: 'UTF-8' },
        // Public verification token for Google Search Console - domain ownership proof
        { name: 'google-site-verification', content: 'NGqvwihMfgCkh6zE_m0cqDXUiknF3C_A8k_enxPx624' },
        { name: 'format-detection', content: 'telephone=no' },
        { name: 'apple-mobile-web-app-capable', content: 'yes' },
        { name: 'apple-mobile-web-app-status-bar-style', content: 'black-translucent' },
        { name: 'mobile-web-app-capable', content: 'yes' },
        { httpEquiv: 'X-UA-Compatible', content: 'IE=edge' },
        // CSP is defined as a meta tag in index.html (single source of truth).
        // Do NOT inject a second CSP here — two competing CSPs cause the browser
        // to enforce the intersection, silently blocking scripts whose origins
        // are missing from whichever is more restrictive.
        // X-Frame-Options is not injected: browsers ignore it in a <meta> tag
        // (and log an error); the real protection is the HTTP header
        // `x-frame-options: DENY` that the host already sends.
        { httpEquiv: 'X-Content-Type-Options', content: 'nosniff' },
        { httpEquiv: 'X-XSS-Protection', content: '1; mode=block' },
        { httpEquiv: 'Strict-Transport-Security', content: 'max-age=31536000; includeSubDomains; preload' },
        { httpEquiv: 'Cross-Origin-Opener-Policy', content: 'same-origin' },
        { httpEquiv: 'Cross-Origin-Embedder-Policy', content: 'unsafe-none' },
        { httpEquiv: 'Cross-Origin-Resource-Policy', content: 'same-origin' },
        { httpEquiv: 'Referrer-Policy', content: 'strict-origin-when-cross-origin' },
        { httpEquiv: 'Permissions-Policy', content: 'geolocation=(), microphone=(), camera=()' },
        { name: 'robots', content: 'index, follow, max-image-preview:large' }
      ];

    metaTags.forEach(tag => {
      let existing = null;
      if (tag.name) {
        existing = document.querySelector(`meta[name="${tag.name}"]`);
      } else if (tag.httpEquiv) {
        existing = document.querySelector(`meta[http-equiv="${tag.httpEquiv}"]`);
      }

      if (!existing) {
        const meta = document.createElement('meta');
        if (tag.name) meta.name = tag.name;
        if (tag.httpEquiv) meta.httpEquiv = tag.httpEquiv;
        meta.content = tag.content;
        document.head.appendChild(meta);
      }
    });

    // No web font is loaded: the site renders in the system font stack, and
    // the former Poppins stylesheet was a render-blocking request for a
    // family no rule used.

    // Prioritize only critical preconnects
    const criticalResources = [
      { rel: 'preconnect', href: 'https://qtrypzzcjebvfcihiynt.supabase.co', crossOrigin: false }
    ];

    criticalResources.forEach(({ rel, href, crossOrigin }) => {
      if (!document.querySelector(`link[rel="${rel}"][href="${href}"]`)) {
        const link = document.createElement('link');
        link.rel = rel;
        link.href = href;
        if (crossOrigin) link.crossOrigin = 'anonymous';
        document.head.appendChild(link);
      }
    });

        // Add priority preload hints for LCP images
        const lcpImages = {
          'Services': 'https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/697e88817b05b4051a928e3f/97657042f_33363942_975460192617351_6697685401403916288_o.jpg'
        };

        const lcpImage = lcpImages[currentPageName];
        if (lcpImage && !document.querySelector(`link[rel="preload"][href="${lcpImage}"]`)) {
          const link = document.createElement('link');
          link.rel = 'preload';
          link.as = 'image';
          link.href = lcpImage;
          link.fetchPriority = 'high';
          document.head.appendChild(link);
        }

    // Optimize animations for mobile (disable in reduced motion)
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      document.documentElement.style.setProperty('--framer-motion-duration', '0s');
    }

    // Defer performance monitoring and enable code splitting
    if (import.meta.env.PROD) {
      scheduleTask(() => {
        // Remove console logs in production for smaller bundle
        if (typeof window !== 'undefined') {
          window.console.log = () => {};
          window.console.warn = () => {};
          window.console.info = () => {};
        }
      }, { timeout: 1000 });
    }
  }, [currentPageName]);

  return (
    <>
    <a href="#main-content" className="skip-to-main">Skip to main content</a>
    <SignupConversion />
    {/* Preserve the contact-data privacy boundary before optional SDK effects. */}
    {!isAdmin && <span hidden data-sensitive-contact-boundary="site-contact" aria-hidden="true" />}
    {/* The former GeoRestriction wrapper (third-party IP lookup that blocked
        rendering and would have turned away VPN / iCloud Private Relay
        visitors) was removed Nov 2026. */}
      <AIPersonalizationProvider>
        <NoIndexGuard />
        {!isAdmin && <>
          <BreadcrumbSchema />


          <GlobalPageSchema />
          {!suppressEngagement && afterPaint && <Suspense fallback={null}><OnboardingTour /></Suspense>}
          <ImageProtection />
        </>}
        {!isAdmin && <WidgetErrorBoundary name="CookieConsent">
          <CookieConsent />
        </WidgetErrorBoundary>}
        {/* Force own compositing layer to prevent Chromium white-screen on scroll */}
        <div ref={contentRef} style={{ transform: 'translateZ(0)', isolation: 'isolate' }} className={isServiceLanding ? 'service-landing' : undefined}>
          {children}
        </div>
        {!isAdmin && <>
          {!suppressEngagement && afterPaint && <>
            {HAS_SESSION && <WidgetErrorBoundary name="PersonalizedBlogRecommendations">
              <Suspense fallback={null}>
                <PersonalizedBlogRecommendations />
              </Suspense>
            </WidgetErrorBoundary>}
            <WidgetErrorBoundary name="FloatingCTA">
              <Suspense fallback={null}>
                <FloatingCTA page={currentPageName} />
              </Suspense>
            </WidgetErrorBoundary>
            <WidgetErrorBoundary name="SMSWidget">
              <Suspense fallback={null}>
                <SMSWidget />
              </Suspense>
            </WidgetErrorBoundary>
            <WidgetErrorBoundary name="SmartCTAEngine">
              <Suspense fallback={null}>
                <SmartCTAEngine pageName={currentPageName} />
              </Suspense>
            </WidgetErrorBoundary>
          </>}
          {serviceLanding && <WidgetErrorBoundary name="ServiceMobileBar">
            <ServiceMobileBar />
          </WidgetErrorBoundary>}
        </>}
      </AIPersonalizationProvider>
    </>
  );
}